import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useRef} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom"; // Import useNavigate
import CustomNotification from "../Games/DefaultLottery/components/CustomNotification";
import {postCreateTicketService} from "../services/postCreateTicket.service";
import {classifyTicketError, OUTCOME_LIMIT} from "../utils/lotteryTicketRecovery";

// Shared lottery ticket purchase hook.
//
// Backward compatible: it always returns the historical keys
// ({postCreateTicket, data, isError, isPending, isSuccess, error}) so every
// existing consumer (DefaultLottery, TerminalBoard, TripleBoard, RepeatPlayModal,
// the public QuickBuyModal) keeps working. It additionally exposes `reset` (to
// clear a stale success/error before the next submission) and `outcomeRef` (the
// last normalized classification of an error, produced by the pure recovery
// helpers) so a recovery-aware caller can render structured limit issues inline
// instead of relying on delayed per-selection toasts.
// opts.handleStructuredLimits: when true, the caller renders structured limit
// issues itself (DefaultLottery recovery modal) and the hook suppresses the
// legacy delayed per-selection toasts for handled LOTTERY_LIMIT_EXCEEDED
// responses. Callers that have not adopted the recovery UI must leave this off so
// they keep their existing per-item messaging even if the backend now also emits
// a structured `code`.
const useCreateTicket = opts => {
  const handleStructuredLimits = opts?.handleStructuredLimits === true;
  const navigate = useNavigate(); // Initialize useNavigate
  const queryClient = useQueryClient();

  const {t} = useTranslation();

  // Last normalized outcome. Kept in a ref so recovery-aware callers read it
  // synchronously in their error effect without waiting for a re-render.
  const outcomeRef = useRef(null);

  const {
    data,
    isError,
    isPending,
    error,
    mutate: postCreateTicket,
    reset,
    isSuccess,
  } = useMutation({
    mutationKey: ["create-lottery-ticket"],
    mutationFn: postCreateTicketService,
    onSuccess: response => {
      outcomeRef.current = null;
      // Extract the data from the response
      const data = response?.data?.data || response?.data || response;
      const rejectedBets = data?.rejected_bets;
      const _totalRequested = data?.total_requested;
      const _totalAccepted = data?.total_accepted;

      // Check if there are any rejected bets
      const hasRejectedBets = rejectedBets && Array.isArray(rejectedBets) && rejectedBets.length > 0;

      if (hasRejectedBets) {
        // Show warning for partial acceptance
        CustomNotification.warning({
          message: "Algunas apuestas no fueron aceptadas",
          action: {
            text: "Ver mis jugadas",
            onClick: () => navigate("/lottery/tickets"),
          },
          duration: 0, // Requires manual dismissal
        });

        // Show individual notification for each rejected bet with delay
        rejectedBets.forEach((bet, index) => {
          setTimeout(
            () => {
              // Determine identifier: use number if available, otherwise use sign
              const identifier = bet.number || bet.sign_id || `Jugada ${index + 1}`;

              CustomNotification.warning({
                message: `El número ${identifier} no tiene cupo de venta, por lo que no fue procesado en la venta del ticket`,
                duration: 0,
              });
            },
            (index + 1) * 800
          ); // 800ms delay between each notification
        });
      } else {
        // All bets accepted - show success
        CustomNotification.success({
          message: t("lotteryTicketCreatedMessage"),
          action: {
            text: "Ver mis jugadas",
            onClick: () => navigate("/lottery/tickets"),
          },
        });
      }

      queryClient.invalidateQueries(["lottery_tickets"]);
      // Don't navigate automatically since the user can choose to click the button
    },
    onError: error => {
      const outcome = classifyTicketError(error);
      outcomeRef.current = outcome;

      // Extract backend error data (legacy fallback path).
      const errorData = error?.response?.data || error?.data;
      const backendMessage = errorData?.message || error?.message;
      const figures = errorData?.figures;
      const items = errorData?.items; // For number-based games (Triple, Terminal)

      // A structured limit rejection is rendered inline by the recovery modal.
      // Only skip the delayed per-selection toasts for callers that opted in; every
      // other consumer keeps the legacy per-item messaging below.
      if (handleStructuredLimits && outcome.kind === OUTCOME_LIMIT) {
        if (backendMessage) {
          CustomNotification.error({message: backendMessage, duration: 0});
        }
        return;
      }

      // Legacy (and opted-in callers that fall through for non-limit codes):
      // original per-item delayed notifications so games without the recovery modal
      // continue to explain each failure.
      const status = error?.response?.status || error?.status;
      const limitData = figures || items;
      if (status === 400 && limitData && typeof limitData === "object") {
        if (backendMessage) {
          CustomNotification.error({message: backendMessage, duration: 0});
        }
        const itemNumbers = Object.keys(limitData);
        itemNumbers.forEach((itemNumber, index) => {
          const itemError = limitData[itemNumber];
          setTimeout(
            () => {
              const label = figures ? `Figura ${itemNumber}` : `Número ${itemNumber}`;
              CustomNotification.error({message: `${label}: ${itemError}`, duration: 0});
            },
            (index + 1) * 1000
          );
        });
        return;
      }

      if (outcome.message) {
        CustomNotification.error({message: outcome.message, duration: 0});
      } else if (backendMessage) {
        CustomNotification.error({message: backendMessage, duration: 0});
      } else {
        CustomNotification.error({message: t("lotteryTicketCreatedMessageError"), duration: 0});
      }
    },
  });

  return {
    postCreateTicket,
    data,
    isError,
    isPending,
    isSuccess,
    error,
    reset,
    outcomeRef,
  };
};

export default useCreateTicket;
